"use client";

// French copy of components/platformHowTo (FR-1). The per-platform guide
// pages exist only in English, so the panel button says so.

import React, { useState } from "react";
import { StyledSection } from "../platformHowTo/platformHowTo.styles";
import { trackEvent } from '@/lib/track';

interface IPlatformGuide {
  platform: string;
  // Optional. Omitted by hubs whose widget has no published platform spoke,
  // where the panel CTA would otherwise point at a 404. The steps still render.
  href?: string;
  steps: string[];
}

interface IPlatformHowToFrProps {
  title: string;
  eyebrow?: string;
  // Kept for parity with the English props. The French button text avoids the
  // widget name so it does not have to agree in gender with it.
  widgetName?: string;
  guides: IPlatformGuide[];
  tabEventName?: string;
  ctaEventName?: string;
}

export const PlatformHowToFr = ({
  title,
  eyebrow = "Guides par plateforme",
  guides,
  tabEventName,
  ctaEventName,
}: IPlatformHowToFrProps) => {
  const [openIndex, setOpenIndex] = useState<number>(0);
  if (!guides.length) return null;

  return (
    <StyledSection>
      <div className="inner">
        <header>
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          <p className="lead">
            Choisissez votre plateforme. Chacune prend moins de 4 minutes.
          </p>
        </header>

        <div className="tabs" role="tablist">
          {guides.map((g, i) => (
            <button
              key={g.platform}
              className={`tab ${i === openIndex ? "active" : ""}`}
              onClick={() => {
                setOpenIndex(i);
                if (tabEventName)
                  trackEvent(tabEventName, { platform: g.platform });
              }}
              role="tab"
              aria-selected={i === openIndex}
              aria-controls={`platform-panel-${i}`}
            >
              {g.platform}
            </button>
          ))}
        </div>

        <div className="panels">
          {guides.map((g, i) => (
            <div
              key={g.platform}
              id={`platform-panel-${i}`}
              role="tabpanel"
              hidden={i !== openIndex}
              className="panel"
            >
              <ol>
                {g.steps.map((s, idx) => (
                  <li key={idx}>
                    <span className="step-num">{idx + 1}</span>
                    <span className="step-text">{s}</span>
                  </li>
                ))}
              </ol>
              {g.href && (
                <a
                  className="panel-cta"
                  href={g.href}
                  hrefLang="en"
                  onClick={() => {
                    if (ctaEventName)
                      trackEvent(ctaEventName, { platform: g.platform });
                  }}
                >
                  Ajouter sur {g.platform} (guide en anglais)
                  <span aria-hidden="true">→</span>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </StyledSection>
  );
};
